import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '@services/api';

interface Props {
  token?: string;
}

export function ResetPasswordPage({ token }: Props) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      const response = await api.get<any>(`/api/auth/verify-reset-token?token=${token}`);
      setTokenValid(response.success && response.data?.valid);
      setVerifying(false);
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (password.length < 8) {
      setError('비밀번호는 최소 8자 이상이어야 합니다.');
      return;
    }

    if (!/[A-Z]/.test(password)) {
      setError('비밀번호에 대문자가 포함되어야 합니다.');
      return;
    }

    if (!/[a-z]/.test(password)) {
      setError('비밀번호에 소문자가 포함되어야 합니다.');
      return;
    }

    if (!/[0-9]/.test(password)) {
      setError('비밀번호에 숫자가 포함되어야 합니다.');
      return;
    }

    setLoading(true);

    const response = await api.post<any>('/api/auth/reset-password', {
      token,
      password,
    });

    if (response.success) {
      setSuccess(true);
    } else {
      setError(response.error || '비밀번호 변경에 실패했습니다.');
    }

    setLoading(false);
  };

  if (verifying) {
    return (
      <div class="flex min-h-screen items-center justify-center bg-gray-100">
        <div class="card w-full max-w-md text-center">
          <div class="text-lg">토큰 확인 중...</div>
        </div>
      </div>
    );
  }

  return (
    <div class="flex min-h-screen items-center justify-center bg-gray-100">
      <div class="card w-full max-w-md">
        <div class="mb-6 flex flex-col items-center">
          <img src="/logo.svg" alt="DbitLabs Logo" class="mb-3 h-16 w-16" />
          <h1 class="font-brand text-center text-2xl font-bold">DbitLabs : IssueManager</h1>
        </div>

        <h2 class="mb-6 text-center text-lg font-semibold text-gray-700">비밀번호 재설정</h2>

        {!token || !tokenValid ? (
          <div class="text-center">
            <div class="mb-4 rounded bg-red-100 p-4 text-red-700">
              <p class="font-medium">유효하지 않은 링크입니다</p>
              <p class="mt-2 text-sm">
                링크가 만료되었거나 이미 사용되었습니다. 비밀번호 찾기를 다시 시도해주세요.
              </p>
            </div>
            <a
              href="/forgot-password"
              class="text-sm text-primary-600 hover:text-primary-700 hover:underline"
            >
              비밀번호 찾기로 이동
            </a>
          </div>
        ) : success ? (
          <div class="text-center">
            <div class="mb-4 rounded bg-green-100 p-4 text-green-700">
              <p class="font-medium">비밀번호가 변경되었습니다!</p>
              <p class="mt-2 text-sm">새 비밀번호로 로그인해주세요.</p>
            </div>
            <button
              onClick={() => route('/login')}
              class="btn btn-primary"
            >
              로그인 페이지로 이동
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div class="mb-4">
              <label class="mb-2 block text-sm font-medium text-gray-700">새 비밀번호</label>
              <input
                type="password"
                class="input"
                value={password}
                onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
                required
                minLength={8}
              />
              <p class="mt-1 text-xs text-gray-500">
                최소 8자, 대문자, 소문자, 숫자 포함
              </p>
            </div>

            <div class="mb-4">
              <label class="mb-2 block text-sm font-medium text-gray-700">비밀번호 확인</label>
              <input
                type="password"
                class="input"
                value={confirmPassword}
                onInput={(e) => setConfirmPassword((e.target as HTMLInputElement).value)}
                required
                minLength={8}
              />
            </div>

            {error && (
              <div class="mb-4 rounded bg-red-100 p-3 text-sm text-red-700">{error}</div>
            )}

            <button type="submit" class="btn btn-primary w-full text-white" disabled={loading}>
              {loading ? '처리중...' : '비밀번호 변경'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
